import axios from 'axios';
import type { StorageValue } from 'axios-cache-interceptor';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useIsMounted } from '..';
import type { RequestState } from './useRequestReducer';
import { useRequestReducer, RequestActions } from './useRequestReducer';

type HookReturnValue<T> = [RequestState<T>, (params?: MakeRequest) => Promise<T | null>, RequestReset];
type MakeRequest = { cache?: { override?: boolean; ttl?: number; }; [key: string]: any; };
type RequestReset = () => void;

type RequestCallback<T> = ({
  apiCacheData,
  cache,
  requestInProgress,
  ...params
}: {
  apiCacheData?: any;
  cache?: { override?: boolean; ttl?: number };
  requestInProgress: MutableRefObject<boolean>;
}) => Promise<T | null>;

type Options = {
  apiCache?: StorageValue;
  defaultCache?: { override?: boolean; ttl?: number };
  onError?: (error: Error | unknown) => void;
  preserveDataWhileLoading?: boolean;
  requestOnMount?: boolean;
};

function useRequest<T>(
  request: RequestCallback<T>,
  { apiCache, defaultCache, preserveDataWhileLoading = false, requestOnMount = false, onError }: Options = {}
): HookReturnValue<T> {
  const [state, dispatch] = useRequestReducer<T>({ isLoading: requestOnMount });
  const requestInProgress = useRef(false);
  const isMounted = useIsMounted();

  const resetRequest = useCallback(() => {
    requestInProgress.current = false;

    dispatch({ type: RequestActions.RESET });
  }, [dispatch]);

  const makeRequest = useCallback(
    async ({ cache, ...params }: MakeRequest = {}) => {
      if (requestInProgress.current || !isMounted.current) {
        return null;
      }

      requestInProgress.current = true;

      const apiCacheOption = cache || defaultCache;
      const requestAction = preserveDataWhileLoading ? RequestActions.REQUEST_PRESERVE_DATA : RequestActions.REQUEST;

      const apiCacheData =
        (!apiCacheOption?.override && apiCache?.state === 'cached') ? apiCache?.data?.data : undefined;

      if (!apiCacheData) {
        dispatch({ type: requestAction });
      }

      try {
        // Pass the `apiCacheData` to the request so we can manually return it, otherwise it could expire in between
        // checking if there's a cache and letting axios cache interceptor return it automatically.
        const data = await request({ apiCacheData, cache: apiCacheOption, requestInProgress, ...params });

        if (requestInProgress.current) {
          dispatch({ payload: data, type: RequestActions.SUCCESS });
          return data;
        }

        return null;
      } catch (error) {
        if (requestInProgress.current) {
          if (!axios.isCancel(error)) {
            console.error(error);
          }

          const newError = onError?.(error);

          dispatch({ payload: newError || error, type: RequestActions.ERROR });
        }

        return null;
      } finally {
        requestInProgress.current = false;
      }
    },
    [
      apiCache?.state,
      apiCache?.data?.data,
      defaultCache,
      dispatch,
      isMounted,
      onError,
      preserveDataWhileLoading,
      request,
      requestInProgress,
    ]
  );

  useEffect(() => {
    if (requestOnMount) {
      makeRequest();
    }
    // We want to make sure we only run this once on mount hence why the dependency array is empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    requestInProgress.current = false;
  }, []);

  return [state, makeRequest, resetRequest];
}

export type { RequestCallback };
export { useRequest };
